import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Receipts } from "@/lib/api";
import { Printer, ArrowLeft, FileDown } from "lucide-react";
import { toast } from "sonner";
import { BillyMark } from "@/components/BillyLogo";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
  });

const ReceiptsExport = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const date_from = params.get("from");
  const date_to = params.get("to");
  const label = params.get("label") || "Receipts";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!date_from || !date_to) return;
    Receipts.exportData(date_from, date_to)
      .then(setData)
      .catch(() => toast.error("Could not load export data."))
      .finally(() => setLoading(false));
  }, [date_from, date_to]);

  if (!date_from || !date_to) {
    return (
      <div className="text-sm text-neutral-600">
        Missing date range. Go back and choose a quarter or custom range.
      </div>
    );
  }

  return (
    <div data-testid="receipts-export" className="bg-white text-black min-h-screen">
      {/* Action bar — hidden when printing */}
      <div className="print:hidden border-b-2 border-black bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/receipts")}
            data-testid="back-btn"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-black"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="download-csv-btn"
              onClick={() => Receipts.exportCsv(date_from, date_to)}
              className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-black hover:text-white transition-colors inline-flex items-center gap-2"
            >
              <FileDown className="h-4 w-4" /> Download CSV
            </button>
            <button
              data-testid="print-btn"
              onClick={() => window.print()}
              className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
            >
              <Printer className="h-4 w-4" /> Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10 print:p-0">
        {/* Header */}
        <div className="flex items-start justify-between gap-6 border-b-2 border-black pb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
              BAS Receipts Summary
            </div>
            <h1 className="font-display font-black text-3xl tracking-tight mt-1">
              {label}
            </h1>
            <div className="text-sm text-neutral-700 mt-2 mono">
              {date_from} → {date_to}
            </div>
          </div>
          <BillyMark size={56} />
        </div>

        {loading ? (
          <div className="text-sm text-neutral-500 mt-10">Loading…</div>
        ) : !data ? (
          <div className="text-sm text-neutral-500 mt-10">No data.</div>
        ) : (
          <>
            {/* Totals */}
            <div className="grid grid-cols-3 gap-4 mt-8" data-testid="totals">
              <div className="border-2 border-black p-5">
                <div className="swiss-label">Receipts</div>
                <div className="font-display font-black text-3xl mono mt-1">
                  {data.count}
                </div>
              </div>
              <div className="border-2 border-black p-5">
                <div className="swiss-label">Total spend (incl. GST)</div>
                <div className="font-display font-black text-3xl mono mt-1">
                  {fmt(data.total_amount)}
                </div>
              </div>
              <div className="border-2 border-black p-5 bg-black text-white">
                <div className="swiss-label" style={{ color: "#ddd" }}>
                  GST Paid (1B credit)
                </div>
                <div className="font-display font-black text-3xl mono mt-1">
                  {fmt(data.total_gst)}
                </div>
              </div>
            </div>

            {/* Category breakdown */}
            <h2 className="font-display font-extrabold text-xl uppercase tracking-widest mt-10 border-b-2 border-black pb-2">
              By category
            </h2>
            <table
              className="w-full mt-4 text-sm"
              data-testid="cat-table"
            >
              <thead>
                <tr className="text-left border-b border-neutral-300">
                  <th className="py-2 swiss-label">Category</th>
                  <th className="py-2 swiss-label text-right">Count</th>
                  <th className="py-2 swiss-label text-right">Total</th>
                  <th className="py-2 swiss-label text-right">GST</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.by_category)
                  .sort((a, b) => b[1].total - a[1].total)
                  .map(([cat, v]) => (
                    <tr key={cat} className="border-b border-neutral-100">
                      <td className="py-2 font-bold">{cat}</td>
                      <td className="py-2 mono text-right">{v.count}</td>
                      <td className="py-2 mono text-right">{fmt(v.total)}</td>
                      <td className="py-2 mono text-right">{fmt(v.gst)}</td>
                    </tr>
                  ))}
                {Object.keys(data.by_category).length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-4 text-neutral-500">
                      No receipts in this range.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Itemised list */}
            {data.items.length > 0 && (
              <>
                <h2 className="font-display font-extrabold text-xl uppercase tracking-widest mt-10 border-b-2 border-black pb-2">
                  Itemised receipts
                </h2>
                <table
                  className="w-full mt-4 text-sm"
                  data-testid="items-table"
                >
                  <thead>
                    <tr className="text-left border-b border-neutral-300">
                      <th className="py-2 swiss-label">Date</th>
                      <th className="py-2 swiss-label">Vendor</th>
                      <th className="py-2 swiss-label">Category</th>
                      <th className="py-2 swiss-label">Method</th>
                      <th className="py-2 swiss-label text-right">Total</th>
                      <th className="py-2 swiss-label text-right">GST</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((r) => (
                      <tr
                        key={r.id}
                        className="border-b border-neutral-100 align-top"
                      >
                        <td className="py-2 mono">{r.receipt_date}</td>
                        <td className="py-2 font-bold">
                          {r.vendor}
                          {r.notes && (
                            <div className="text-[11px] text-neutral-500 font-normal mt-0.5">
                              {r.notes}
                            </div>
                          )}
                        </td>
                        <td className="py-2">{r.category}</td>
                        <td className="py-2 text-[12px] text-neutral-600">
                          {r.payment_method || "—"}
                        </td>
                        <td className="py-2 mono text-right">
                          {fmt(r.total_amount)}
                        </td>
                        <td className="py-2 mono text-right">
                          {fmt(r.gst_amount)}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-black font-bold">
                      <td className="py-3">TOTAL</td>
                      <td colSpan={3}></td>
                      <td className="py-3 mono text-right">
                        {fmt(data.total_amount)}
                      </td>
                      <td className="py-3 mono text-right">
                        {fmt(data.total_gst)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}

            <div className="mt-10 pt-6 border-t-2 border-black text-[11px] uppercase tracking-[0.25em] text-neutral-500 text-center">
              Generated by Billy · The Outdoor Blind Company ·{" "}
              {new Date().toLocaleDateString("en-AU")}
            </div>
          </>
        )}
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 18mm; }
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .print\\:p-0 { padding: 0 !important; }
        }
      `}</style>
    </div>
  );
};

export default ReceiptsExport;
